import { useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/use-queries";
import { forumError } from "@/hooks/use-forum";
import {
  followPath,
  useDiscoveryFollows,
  type FollowKind,
} from "./discovery-api";

export function FollowButton({
  kind,
  targetKey,
  label,
}: {
  kind: FollowKind;
  targetKey: string;
  label: string;
}) {
  const { data: me } = useMe();
  const follows = useDiscoveryFollows(me?.id);
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const following = follows.data?.some(
    (f) => f.kind === kind && f.targetKey === targetKey
  );
  if (!me) return null;

  async function toggle() {
    setBusy(true);
    setError("");
    try {
      if (following) await api.delete(followPath(kind, targetKey));
      else await api.put(followPath(kind, targetKey));
      await cache.invalidateQueries({ queryKey: ["discovery-follows", me.id] });
      await cache.invalidateQueries({ queryKey: ["discovery-feed", me.id] });
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      <Button
        variant="outline"
        size="sm"
        disabled={busy || follows.isPending || follows.isError}
        aria-pressed={!!following}
        onClick={toggle}
      >
        {following ? (
          <BellOff size={14} className="mr-2" />
        ) : (
          <Bell size={14} className="mr-2" />
        )}
        {busy
          ? "Saving…"
          : following
          ? `Following ${label}`
          : `Follow ${label}`}
      </Button>
      {(error || follows.isError) && (
        <p role="alert" className="text-xs text-destructive">
          {error || "Could not load follow state."}{" "}
          {follows.isError && (
            <button className="underline" onClick={() => follows.refetch()}>
              Retry
            </button>
          )}
        </p>
      )}
    </div>
  );
}
